import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import i18next from 'i18next';
import axiosInstance from '../../config/axiosConfig';
import { ToggleButton } from 'primereact/togglebutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { debounce } from 'lodash';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';
import useUserStore from '../../store/useUserStore';

const t = (i18nKey) => i18next.t(`userDashboard.preferences.notifications.${i18nKey}`);

const notificationLabels = {
    urgent_alerts: <>{t('urgent_alerts')}</>,
    system_maintenance: <>{t('system_maintenance')}</>,
    test_completed: <>{t('test_completed')}</>,
    test_list_completed: <>{t('test_list_completed')}</>,
    new_products: <>{t('new_products')}</>,
    product_updates: <>{t('product_updates')}</>,
    special_offers: <>{t('special_offers')}</>,
    platform_updates: <>{t('platform_updates')}</>,
    security_updates: <>{t('security_updates')}</>,
    billing_alerts: <>{t('billing_alerts')}</>,
    reports_ready: <>{t('reports_ready')}</>,
    account_status: <>{t('account_status')}</>,
};

const notificationDescriptions = Object.keys(notificationLabels).reduce((acc, key) => {
    acc[key] = t(`descriptions.${key}`);
    return acc;
}, {});

const categoryLabels = {
    tests: t('categories.tests'),
    promotion: t('categories.promotion'),
    system: t('categories.system'),
};

const NotificationToggle = ({ checked, onChange, disabled }) => (
    <ToggleButton
        checked={checked ?? false} 
        onChange={onChange}
        onLabel={t('toggle.yes')}
        offLabel={t('toggle.no')}
        onIcon="pi pi-check"
        offIcon="pi pi-times"
        disabled={disabled}
        className={clsx('invitation-toggle-button is-fullwidth', { 'disabled-toggle': disabled })}
    />
);

const NotificationsPreferences = () => {
    const [notifications, setNotifications] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const toast = useRef(null);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const user = useUserStore((state) => state.user);

    useEffect(() => {
        axiosInstance.get(`/dashboard/preferences/${selectedCompany?.company_id}/getDetails`).then(response => {
            setNotifications(response.data);
            setIsLoading(false);
        }).catch(error => {
            console.error('Error fetching notification preferences:', error);
            setNotifications({
                urgent_alerts: { category: null, email: false, sms: false, whatsapp: null },
                system_maintenance: { category: null, email: false, sms: false, whatsapp: false },
                test_completed: { category: 'tests', email: false, sms: false, whatsapp: false },
                test_list_completed: { category: 'tests', email: false, sms: false, whatsapp: false },
                new_products: { category: 'promotion', email: false, sms: false, whatsapp: false },
                product_updates: { category: 'promotion', email: false, sms: false, whatsapp: false },
                special_offers: { category: 'promotion', email: false, sms: false, whatsapp: false },
                platform_updates: { category: 'system', email: true, sms: false, whatsapp: null },
                security_updates: { category: 'system', email: true, sms: true, whatsapp: null },
                billing_alerts: { category: 'system', email: true, sms: false, whatsapp: null },
                reports_ready: { category: 'system', email: true, sms: false, whatsapp: null },
                account_status: { category: 'system', email: true, sms: true, whatsapp: null },
            });
            setIsLoading(false);
        });
    }, [selectedCompany?.company_id]);

    const debouncedSavePreferences = useRef(
        debounce((updatedNotifications) => {
            axiosInstance.post(`/dashboard/preferences/${selectedCompany?.company_id}/update`, {
                preferences: updatedNotifications,
            }).then(response => {
                setNotifications(response.data);
                toast.current.show({ severity: 'success', summary: 'Préférences', detail: t('success') });
            }).catch(error => {
                console.error('Error updating notification preferences:', error);
            });
        }, 1000)
    ).current;

    const handleNotificationChange = (key, channel, value) => {
        setNotifications(prev => {
            const updated = { ...prev[key], [channel]: value };

            if (value) {
                ['email', 'sms', 'whatsapp'].forEach(ch => {
                    if (ch !== channel && prev[key][ch] !== null) {
                        updated[ch] = false;
                    }
                });
            }

            const newNotifications = { ...prev, [key]: updated };

            debouncedSavePreferences(newNotifications);

            return newNotifications;
        });
    };

    const isChannelDisabled = (channel, whatsappValue) => {

        if (!user?.phone_number) {
            return channel === 'sms' || channel === 'whatsapp';
        }

        if (channel === 'whatsapp') {
            return whatsappValue === null;
        }
        return false;
    };

    const groupedNotifications = Object.entries(notifications).reduce((groups, [key, value]) => {
        const category = value.category || 'uncategorized';
        if (!groups[category]) groups[category] = [];
        groups[category].push({ key, ...value });
        return groups;
    }, {});

    return (
        <div className="notification-settings">
            <Toast ref={toast} />
            {isLoading ? (
                <ProgressSpinner style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }} />
            ) : (
                <div className="table-container">
                    <table className="table is-fullwidth notifications-preferences-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                {['Email', 'SMS', 'WhatsApp'].map((channel, idx) => (
                                    <th key={idx} className='has-text-centered'>
                                        <i className={clsx('pi', { 'pi-whatsapp': channel.toLowerCase() === 'whatsapp', 'pi-envelope': channel.toLowerCase() === 'email', 'pi-comment': channel.toLowerCase() === 'sms' })}></i> {channel}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>

                            {groupedNotifications.uncategorized &&
                                groupedNotifications.uncategorized.map(({ key, email, sms, whatsapp }) => (
                                    <tr key={key} style={{ height: '3rem' }}>
                                        <td>
                                            <span id={`tooltip_${key}`}>
                                                {notificationLabels[key] || key}
                                                {notificationDescriptions[key] && (
                                                    <>
                                                        <i className="pi pi-info-circle ml-2" id={`info_${key}`}></i>
                                                        <Tooltip
                                                            target={`#info_${key}`}
                                                            content={notificationDescriptions[key]}
                                                            position="top"
                                                            mouseTrack
                                                            mouseTrackTop={10}
                                                            pt={{
                                                                arrow: {
                                                                    style: { display: 'none' }
                                                                }
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        {['email', 'sms', 'whatsapp'].map(channel => (
                                            <td key={channel} className='has-text-centered'>
                                                <NotificationToggle
                                                    checked={channel === 'whatsapp' ? whatsapp : channel === 'email' ? email : sms}
                                                    onChange={(e) => handleNotificationChange(key, channel, e.value)}
                                                    disabled={isChannelDisabled(channel, channel === 'whatsapp' ? whatsapp : null)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}

                            {Object.entries(groupedNotifications).filter(([category]) => category !== 'uncategorized').map(([category, items]) => (
                                <React.Fragment key={category}>
                                    <tr className="category-header">
                                        <td colSpan="4" className="is-size-5 has-text-weight-bold py-3">
                                            <span className="mr-2">•</span> {categoryLabels[category] || category}
                                        </td>
                                    </tr>

                                    {items.map(({ key, email, sms, whatsapp }) => (
                                        <tr key={key} style={{ height: '3rem' }}>
                                            <td>
                                                <span id={`tooltip_${key}`}>
                                                    {notificationLabels[key] || key}
                                                    {notificationDescriptions[key] && (
                                                        <>
                                                            <i className="pi pi-info-circle ml-2" id={`info_${key}`}></i>
                                                            <Tooltip
                                                                target={`#info_${key}`}
                                                                content={notificationDescriptions[key]}
                                                                position="top"
                                                                mouseTrack
                                                                mouseTrackTop={10}
                                                                pt={{
                                                                    arrow: {
                                                                        style: { display: 'none' }
                                                                    }
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </span>
                                            </td>
                                            {['email', 'sms', 'whatsapp'].map(channel => (
                                                <td key={channel} className='has-text-centered'>
                                                    <NotificationToggle
                                                        checked={channel === 'whatsapp' ? whatsapp : channel === 'email' ? email : sms}
                                                        onChange={(e) => handleNotificationChange(key, channel, e.value)}
                                                        disabled={isChannelDisabled(channel, channel === 'whatsapp' ? whatsapp : null)}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default NotificationsPreferences;